/**
 * Score below which a single evaluation counts as a failure — the heatmap
 * scorecard's threshold marker, and the bar the triage rail and the KPI "needs
 * attention" count measure results against. One source of truth across the
 * Insights views so the notion of "bad" stays consistent. Frontend constant for
 * now; a per-workspace setting is a possible follow-up.
 */
export const ATTENTION_SCORE_THRESHOLD = 0.7;

/**
 * How often a result group has to fail before triage surfaces it: the share of
 * its evaluations scoring below ATTENTION_SCORE_THRESHOLD.
 *
 * Severity used to compare a group's *mean* score to that threshold, which
 * answered a different question than the UI claimed to answer. A criterion
 * averaging 0.78 can hide thousands of failures, so the unfiltered page — where
 * every segment and every day is averaged together — reported "nothing needs
 * attention" while results were plainly failing, and only owned up once a
 * filter narrowed the population enough to drag the mean under the line
 * (#1492). A rate is a claim about results, which is what the tile and the rail
 * actually say.
 */
export const ATTENTION_FAIL_RATE_CRITICAL = 0.1;
export const ATTENTION_FAIL_RATE_WATCH = 0.05;

/**
 * Fewest evaluations a result group needs before its failure rate is read as
 * signal rather than noise (2 failures out of 3 is not a 67% failure rate).
 *
 * A rate needs a sample floor, not a share of the corpus. The floor this
 * replaces was 1% of `total_evaluations` — a number the API computes with a
 * separate uncapped `COUNT(*)`, while the series it filtered covers only the
 * most recent INSIGHTS_MATCH_CAP (100k) rows across the top INSIGHTS_CRITERIA_CAP
 * (10) criteria. Past 100k evaluations that floor was measured against a
 * population up to 10x larger than the cells it was suppressing, so on a busy
 * workspace it quietly dropped real failures and stopped only once a filter
 * brought the match set back under the cap (#1492).
 */
export const MIN_ISSUE_EVALUATIONS = 20;

/**
 * How many tag values the grouped section renders, and the choices offered for
 * it. A customer with 40 brands was silently shown 10 with no way to see the
 * rest (#1414); the selector makes the limit visible and raisable. The options
 * must stay within the API's own ceiling (INSIGHTS_GROUP_CAP_MAX = 50), and the
 * default matches the server's so the first render needs no extra param.
 */
export const GROUP_LIMIT_OPTIONS = [10, 25, 50];
export const DEFAULT_GROUP_LIMIT = GROUP_LIMIT_OPTIONS[0];

/**
 * How many values a tag filter dropdown fetches for its key. The dropdown
 * searches this window client-side, so the window has to be big enough to hold
 * a real customer's whole org list — the server keeps the most-frequent values
 * and cuts the rest (#1415). Sent explicitly rather than leaning on the server
 * default (100), because the dropdown also uses it to say when what it shows is
 * a partial list. Must stay within the API ceiling
 * (MAX_METADATA_VALUES_LIMIT = 1000).
 */
export const METADATA_VALUE_LIMIT = 500;
